import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getBankLogo } from '../data/logos';
import { formatDate, formatDateTime } from '../utils/date';
import { API_BASE } from '../config';
import { getImpersonationDomains, type ImpersonationDomain } from '../services/api';
import LanguageSwitcher from '../components/LanguageSwitcher';

interface OfficialEntity {
  id: number;
  code: string;
  name: string;
  category: string;
  country: string;
  officialUrls: string[];
  lastVerifiedAt?: string;
  sources?: Array<{
    sourceName: string;
    sourceUrl: string;
    lastUpdatedAt?: string;
  }>;
}

export function EntityDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [entity, setEntity] = useState<OfficialEntity | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonationDomains, setImpersonationDomains] = useState<ImpersonationDomain[]>([]);
  const [impersonationLoading, setImpersonationLoading] = useState(false);

  const loadEntity = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/v1/entities?country=CZ`);
      const data: OfficialEntity[] = await res.json();
      const found = data.find((e) => e.id === Number(id));
      setEntity(found || null);
      
      // Load impersonation domains for this brand
      if (found?.code) {
        setImpersonationLoading(true);
        try {
          const impersonationData = await getImpersonationDomains({
            brandCode: found.code,
            limit: 10,
          });
          setImpersonationDomains(impersonationData.domains);
        } catch (error) {
          console.error('Failed to load impersonation domains:', error);
        } finally {
          setImpersonationLoading(false);
        }
      }
    } catch (error) {
      console.error('Failed to load entity:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadEntity();
  }, [loadEntity]);

  if (loading) {
    return (
      <div className="page-container">
        <div className="content-wrapper--narrow content-center text-gray-500">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="page-container">
        <div className="content-wrapper--narrow content-center">
          <p className="text-gray-500 mb-4">{t('directory.entityNotFound')}</p>
          <Link to="/directory" className="btn-text">
            {t('directory.backToDirectory')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="content-wrapper--narrow">
        <div className="mb-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="btn-text"
            aria-label={t('directory.backToList')}
          >
            ← {t('directory.backToList')}
          </button>
          <LanguageSwitcher />
        </div>

        <div className="card-compact">
          <div className="mb-4">
            {entity.category === 'Bank' && getBankLogo(entity.code) ? (
              <img
                src={getBankLogo(entity.code)!.src}
                alt={t('alt.logoOf', { name: entity.name })}
                className="logo-detail"
                loading="lazy"
                width={96}
                height={96}
              />
            ) : (
              <div className="logo-detail logo-placeholder rounded" aria-hidden="true" />
            )}
          </div>
          <h1 className="page-title--minimal">{entity.name}</h1>
          <p className="text-gray-600 mb-6">
            {entity.category} • {entity.country}
          </p>

          <div className="mb-6">
            <h2 className="section-heading">
              {t('directory.officialUrls')}
            </h2>
            <ul className="space-y-2">
              {entity.officialUrls.map((url, idx) => (
                <li key={idx}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-external--break"
                  >
                    {url}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {entity.lastVerifiedAt && (
            <div className="mb-6">
              <p className="text-sm text-gray-600">
                {t('directory.lastVerified')}:{' '}
                {formatDate(entity.lastVerifiedAt)}
              </p>
            </div>
          )}

          {entity.sources && entity.sources.length > 0 && (
            <div className="mb-6">
              <h2 className="section-heading">
                {t('evidence.sources')}
              </h2>
              <ul className="text-sm text-gray-700">
                {entity.sources.map((source, idx) => (
                  <li key={idx} className="mb-1">
                    <a
                      href={source.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link-external"
                    >
                      {source.sourceName}
                    </a>
                    {source.lastUpdatedAt && (
                      <span className="text-gray-500 ml-2">
                        ({formatDate(source.lastUpdatedAt)})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Impersonation Domains History & Analytics */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="section-heading">
                {t('entityDetail.impersonationHistory.title')}
              </h2>
              <Link
                to={`/impersonation-domains?brandCode=${entity.code.toLowerCase()}`}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {t('entityDetail.impersonationHistory.viewAll')} →
              </Link>
            </div>

            {impersonationLoading ? (
              <p className="text-sm text-gray-500">{t('common.loading')}</p>
            ) : impersonationDomains.length === 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  {t('entityDetail.impersonationHistory.noThreats')}
                </p>
              </div>
            ) : (
              <>
                {/* Analytics Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-gray-900">
                      {impersonationDomains.length}
                    </div>
                    <div className="text-sm text-gray-600">
                      {t('entityDetail.impersonationHistory.totalDetections')}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-gray-900">
                      {impersonationDomains.reduce((sum, d) => sum + d.hitCount, 0)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {t('entityDetail.impersonationHistory.totalHits')}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-orange-600">
                      {Math.round(
                        impersonationDomains.reduce((sum, d) => sum + d.finalScore, 0) /
                          impersonationDomains.length
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      {t('entityDetail.impersonationHistory.avgRiskScore')}
                    </div>
                  </div>
                </div>

                {/* Recent Impersonation Attempts */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-700">
                    {t('entityDetail.impersonationHistory.recentAttempts')}
                  </h3>
                  {impersonationDomains.slice(0, 5).map((domain) => (
                    <div
                      key={domain.id}
                      className="bg-orange-50 border border-orange-200 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 mb-1">
                            {domain.observedDomain}
                          </div>
                          <div className="text-sm text-gray-600 mb-2">
                            {domain.reason}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>
                              {t('entityDetail.impersonationHistory.lastSeen')}:{' '}
                              {formatDateTime(domain.lastSeenAt)}
                            </span>
                            <span>
                              {t('entityDetail.impersonationHistory.hits')}: {domain.hitCount}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              domain.finalScore >= 70
                                ? 'bg-red-100 text-red-800'
                                : domain.finalScore >= 30
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {domain.finalScore}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
